import React, { useState } from 'react';
import './Temporadas.css';
import ModalEquipo from './ModalEquipo';
import {
  crearParticipante, actualizarParticipante, borrarParticipante, reordenarParticipantes,
} from '../../services/participantesService';

const MODALIDADES = [
  'DOBLES FEMENINO', 'DOBLES MASCULINO', 'DOBLES MIXTO',
  'INDIVIDUAL FEMENINO', 'INDIVIDUAL MASCULINO',
];
const MODALIDADES_DOBLES = new Set(['DOBLES FEMENINO', 'DOBLES MASCULINO', 'DOBLES MIXTO']);

export default function ParticipantesTab({ temporada, participantes, onRefresh, soloLectura }) {
  const [open, setOpen] = useState({});
  const [modalMod, setModalMod] = useState(null);
  const [editando, setEditando] = useState(null);
  const [drag, setDrag] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [error, setError] = useState(null);

  const deModalidad = (mod) =>
    [...participantes.filter(p => p.modalidad === mod)].sort((a, b) => a.orden - b.orden);

  // Al editar, quitar jugadores exige un motivo de baja (la base de datos lo
  // obliga). No sabemos de antemano si la edición quita a alguien, así que
  // se intenta sin motivo y solo se pide si el backend lo reclama.
  const actualizarConMotivoSiHaceFalta = async (participanteId, datos, motivoBaja) => {
    try {
      await actualizarParticipante(participanteId, {
        nombreEquipo: datos.equipo,
        jugadores: datos.jugadores.map(j => ({ jugadorId: Number(j.id) })),
        ...(motivoBaja ? { motivoBaja } : {}),
      });
    } catch (err) {
      if (err.codigo === 'MOTIVO_BAJA_REQUERIDO') {
        const motivo = window.prompt('Indica el motivo de la baja de los jugadores que quitas del equipo:');
        if (!motivo) throw err;
        await actualizarConMotivoSiHaceFalta(participanteId, datos, motivo);
        return;
      }
      throw err;
    }
  };

  // El nombre y el alias de un jugador se corrigen en el catálogo global desde
  // dentro del modal (independiente de "Guardar"). Tanto la lista de jugadores
  // de un equipo como el nombre de un individual (v_participantes.nombre_mostrar)
  // se leen en vivo del jugador, así que basta con refrescar.
  const onJugadorRenombrado = () => onRefresh();

  const guardarEquipo = async (datos) => {
    const mod = modalMod || editando?.modalidad;
    setError(null);
    try {
      if (editando) {
        await actualizarConMotivoSiHaceFalta(editando.participante_id, datos);
      } else {
        const lista = deModalidad(mod);
        await crearParticipante(temporada, {
          modalidad: mod,
          nombreEquipo: datos.equipo,
          jugadores: datos.jugadores.map(j => ({ jugadorId: Number(j.id) })),
          orden: lista.length + 1,
        });
      }
      setModalMod(null); setEditando(null);
      onRefresh();
    } catch (err) {
      setError(err.message || 'Error al guardar el equipo.');
    }
  };

  const borrar = async (p) => {
    if (!window.confirm(`¿Quitar "${p.nombre_mostrar}" de ${p.modalidad}?`)) return;
    setError(null);
    try {
      await borrarParticipante(p.participante_id);
      onRefresh();
    } catch (err) {
      setError(err.message || 'Error al borrar.');
    }
  };

  const descalificar = async (p) => {
    const nuevoEstado = p.estado === 'DESCALIFICADO' ? 'ACTIVO' : 'DESCALIFICADO';
    const msg = nuevoEstado === 'DESCALIFICADO'
      ? `¿Descalificar "${p.nombre_mostrar}"? Sus puntos contarán como 0 en el ranking.`
      : `¿Revertir descalificación de "${p.nombre_mostrar}"?`;
    if (!window.confirm(msg)) return;
    await actualizarParticipante(p.participante_id, { estado: nuevoEstado });
    onRefresh();
  };

  // Drag & drop para reordenar: un único PUT con el orden final de la
  // modalidad, en vez de un PATCH por participante.
  const onDragStart = (p) => setDrag(p);
  const onDragOver = (e, p) => { e.preventDefault(); setDragOver(p?.participante_id); };
  const onDrop = async (e, target) => {
    e.preventDefault();
    if (!drag || drag.participante_id === target.participante_id) { setDrag(null); setDragOver(null); return; }
    const lista = deModalidad(drag.modalidad);
    const from = lista.findIndex(p => p.participante_id === drag.participante_id);
    const to = lista.findIndex(p => p.participante_id === target.participante_id);
    const reord = [...lista];
    reord.splice(from, 1);
    reord.splice(to, 0, drag);
    setDrag(null); setDragOver(null);
    try {
      await reordenarParticipantes(temporada, drag.modalidad, reord.map(p => p.participante_id));
      onRefresh();
    } catch (err) {
      setError(err.message || 'Error al reordenar.');
    }
  };

  return (
    <div>
      {error && <p className="error-msg">{error}</p>}

      {MODALIDADES.map(mod => {
        const lista = deModalidad(mod);
        const abierto = open[mod] !== false;
        return (
          <div key={mod} className="modalidad-section">
            <div className="modalidad-header" onClick={() => setOpen(o => ({ ...o, [mod]: !abierto }))}>
              <h4>{mod}</h4>
              <span className="count">
                {lista.length} {MODALIDADES_DOBLES.has(mod) ? 'equipos' : 'jugadores'} {abierto ? '▲' : '▼'}
              </span>
            </div>
            {abierto && (
              <div className="modalidad-body">
                {lista.map(p => (
                  <div
                    key={p.participante_id}
                    className={`equipo-card${drag?.participante_id === p.participante_id ? ' dragging' : ''}${dragOver === p.participante_id ? ' drag-over' : ''}`}
                    draggable={!soloLectura}
                    onDragStart={() => onDragStart(p)}
                    onDragOver={e => onDragOver(e, p)}
                    onDrop={e => onDrop(e, p)}
                    onDragEnd={() => { setDrag(null); setDragOver(null); }}
                  >
                    {!soloLectura && <span className="drag-handle">⠿</span>}
                    <div className="equipo-info">
                      <div>
                        <span className="equipo-orden">#{p.orden}</span>
                        <span className="equipo-nombre">{p.nombre_mostrar}</span>
                        {p.estado === 'DESCALIFICADO' && <span className="badge-descalificado">DESCALIFICADO</span>}
                      </div>
                      {MODALIDADES_DOBLES.has(p.modalidad) && (
                        <div className="jugadores-list">
                          {p.jugadores?.map((j) => (
                            <span key={j.jugadorId} className={`jugador-tag${p.estado === 'DESCALIFICADO' ? ' descalificado' : ''}`}>
                              {j.nombre}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {!soloLectura && (
                      <div className="equipo-actions">
                        <button onClick={() => setEditando(p)}>Editar</button>
                        <button
                          onClick={() => descalificar(p)}
                          className={p.estado === 'DESCALIFICADO' ? '' : 'danger'}
                        >
                          {p.estado === 'DESCALIFICADO' ? 'Reactivar' : 'Descalificar'}
                        </button>
                        <button className="danger" onClick={() => borrar(p)}>✕</button>
                      </div>
                    )}
                  </div>
                ))}
                {!soloLectura && (
                  <button
                    className="btn btn-secondary"
                    style={{ marginTop: '.5rem', fontSize: '.82rem' }}
                    onClick={() => setModalMod(mod)}
                  >
                    {MODALIDADES_DOBLES.has(mod) ? '+ Añadir equipo' : '+ Añadir jugador'}
                  </button>
                )}
                {!lista.length && (
                  <p className="rr-empty">
                    Sin {MODALIDADES_DOBLES.has(mod) ? 'equipos' : 'jugadores'} para esta modalidad.
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}

      {(modalMod || editando) && (
        <ModalEquipo
          temporada={temporada}
          ocupados={deModalidad(modalMod || editando?.modalidad)
            .filter(p => p.participante_id !== editando?.participante_id)
            .flatMap(p => (p.jugadores ?? []).map(j => j.jugadorId))}
          modalidad={modalMod || editando?.modalidad}
          equipo={editando}
          onGuardar={guardarEquipo}
          onCerrar={() => { setModalMod(null); setEditando(null); }}
          onJugadorRenombrado={onJugadorRenombrado}
        />
      )}
    </div>
  );
}
